import { fromWire, toWire } from '@core/filters/path-filter/codec';
import { DraftGroup, draftGroup } from '@core/filters/path-filter/draft';
import { printCondition, printFilter } from '@core/filters/path-filter/dsl/printer';
import {
  compatibilityConditionKinds,
  compatibilityContextVocabulary,
  compatibilityVocabulary
} from '@core/filters/path-filter/vocabulary/compatibility.vocabulary';
import { decodeOptionsFor } from '@core/filters/path-filter/vocabulary/vocabulary';
import {
  CompatTarget,
  CompatibilityCondition,
  CompatibilityRequirement,
  RoutePosition
} from '@core/filters/path-filter/wire';
import { JsonSchema } from '@core/models/transformers.model';

/**
 * The condition kinds the panel has a control for. A carrier may publish a kind this build does
 * not know; such a condition must be kept as stored, never decoded into a control that drops it.
 */
const EDITABLE_KINDS: ReadonlySet<string> = new Set<string>([
  'context',
  'route_position',
  'service_time_window'
]);

export interface ServiceWindow {
  start: string;
  end: string;
}

/** A stored condition the panel cannot edit, with its index among the stored conditions. */
export interface KeptCondition {
  condition: CompatibilityCondition;
  at: number;
}

type EditableKind = CompatibilityCondition['kind'];

export interface RequirementDraft {
  target: CompatTarget;
  soft: boolean;
  /** Kept while the type is Hard so switching back to Soft restores it; never written for Hard. */
  penalty: number | null;
  filter: DraftGroup;
  /** An empty group writes no context condition. */
  context: DraftGroup;
  /** No selected position writes no `route_position` condition. */
  positions: RoutePosition[];
  serviceWindow: ServiceWindow | null;
  /** Stored conditions the panel cannot edit, written back verbatim. */
  kept: KeptCondition[];
  /** The stored index of the condition each control was decoded from; absent for a new one. */
  anchors: Partial<Record<EditableKind, number>>;
  /** Stored requirement keys the panel has no control for; the encoded fields win over them. */
  extra: Record<string, unknown>;
}

export type RequirementProblem = 'empty_filter' | 'penalty';

export type EncodedRequirement =
  | { ok: true; requirement: CompatibilityRequirement }
  | { ok: false; problem: RequirementProblem };

export interface RequirementSummary {
  filter: string;
  when: string | null;
}

export function newRequirementDraft(target: CompatTarget): RequirementDraft {
  return {
    target,
    soft: false,
    penalty: null,
    filter: draftGroup(),
    context: draftGroup(),
    positions: [],
    serviceWindow: null,
    kept: [],
    anchors: {},
    extra: {}
  };
}

export function decodeRequirement(
  requirement: CompatibilityRequirement,
  carrier: JsonSchema
): RequirementDraft {
  const kinds = compatibilityConditionKinds(carrier);
  const editable = new Set<string>(
    kinds
      .filter((kind) => kind.enforceable && EDITABLE_KINDS.has(kind.kind))
      .map((kind) => kind.kind)
  );
  const offeredPositions: readonly RoutePosition[] =
    kinds.find((kind) => kind.kind === 'route_position')?.positions ?? [];
  const contextOptions = decodeOptionsFor(compatibilityContextVocabulary(carrier));
  const draft: RequirementDraft = {
    ...newRequirementDraft(requirement.target_entity_type),
    extra: otherRequirementKeys(requirement),
    soft: requirement.violation_penalty != null,
    penalty: requirement.violation_penalty ?? null,
    filter: fromWire(
      requirement.filter,
      decodeOptionsFor(compatibilityVocabulary(carrier, requirement.target_entity_type))
    )
  };
  (requirement.conditions ?? []).forEach((condition, at) => {
    // One control per kind: a second condition of a kind already shown is kept as stored.
    const context =
      condition.kind === 'context' ? fromWire(condition.filter, contextOptions) : null;
    if (
      !editable.has(condition.kind) ||
      draft.anchors[condition.kind] !== undefined ||
      !fitsControl(condition, offeredPositions, context)
    ) {
      draft.kept.push({ condition, at });
      return;
    }
    draft.anchors[condition.kind] = at;
    if (condition.kind === 'context') {
      draft.context = context ?? draft.context;
    } else if (condition.kind === 'route_position') {
      draft.positions = [...condition.positions];
    } else {
      draft.serviceWindow = { start: condition.start, end: condition.end };
    }
  });
  return draft;
}

const REQUIREMENT_KEYS: ReadonlySet<string> = new Set<string>([
  'target_entity_type',
  'filter',
  'violation_penalty',
  'conditions'
]);

function otherRequirementKeys(
  requirement: CompatibilityRequirement
): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(requirement).filter(([key]) => !REQUIREMENT_KEYS.has(key))
  );
}

/**
 * Encoding writes no condition for an empty context, an empty position selection or a half-filled
 * window, and the position control cannot show a position the carrier does not offer. Applying a
 * stored condition of that shape would drop it and widen the requirement, so it is kept as stored.
 */
function fitsControl(
  condition: CompatibilityCondition,
  offeredPositions: readonly RoutePosition[],
  context: DraftGroup | null
): boolean {
  switch (condition.kind) {
    case 'context':
      return context !== null && toWire(context) !== undefined;
    case 'route_position':
      return (
        condition.positions.length > 0 &&
        condition.positions.every((position) => offeredPositions.includes(position))
      );
    case 'service_time_window':
      return condition.start !== '' && condition.end !== '';
  }
}

export function encodeRequirement(draft: RequirementDraft): EncodedRequirement {
  const filter = toWire(draft.filter);
  if (filter === undefined) {
    return { ok: false, problem: 'empty_filter' };
  }
  const penalty = draft.penalty;
  if (draft.soft && !(penalty !== null && Number.isFinite(penalty) && penalty > 0)) {
    return { ok: false, problem: 'penalty' };
  }

  const placed: KeptCondition[] = [...draft.kept];
  const place = (condition: CompatibilityCondition): void => {
    placed.push({
      condition,
      at: draft.anchors[condition.kind] ?? Number.MAX_SAFE_INTEGER
    });
  };
  const context = toWire(draft.context);
  if (context !== undefined) {
    place({ kind: 'context', filter: context });
  }
  if (draft.positions.length > 0) {
    place({ kind: 'route_position', positions: draft.positions });
  }
  const window = draft.serviceWindow;
  if (window && window.start !== '' && window.end !== '') {
    place({ kind: 'service_time_window', start: window.start, end: window.end });
  }
  // A stable sort: new conditions, which share the last slot, keep the order they were placed in.
  const conditions = placed.sort((a, b) => a.at - b.at).map((entry) => entry.condition);

  return {
    ok: true,
    requirement: {
      ...draft.extra,
      target_entity_type: draft.target,
      filter,
      violation_penalty: draft.soft ? penalty : null,
      ...(conditions.length > 0 ? { conditions } : {})
    }
  };
}

export function printConditionText(condition: CompatibilityCondition): string {
  return EDITABLE_KINDS.has(condition.kind)
    ? printOrStored(() => printCondition(condition), condition)
    : JSON.stringify(condition);
}

export function summarizeRequirement(
  requirement: CompatibilityRequirement
): RequirementSummary {
  const conditions = requirement.conditions ?? [];
  return {
    filter: printOrStored(() => printFilter(requirement.filter), requirement.filter),
    when: conditions.length > 0 ? conditions.map(printConditionText).join(' and ') : null
  };
}

/**
 * The stored value is whatever the row holds, not necessarily a filter tree, and the printer throws on
 * a malformed one. A summary must not take the entity form down, so it shows the stored JSON instead.
 */
function printOrStored(print: () => string, stored: unknown): string {
  try {
    return print();
  } catch {
    return JSON.stringify(stored);
  }
}
